import ShopType from "@/enum/ShopType";
import OrderSort from "@/scenes/OrderManagement/enum/OrderSort";
import OrderTab from "../../enum/OrderTab";
import globalStore from "@/store";
import AppVersion from "@/enum/AppVersion";
import { ShippingDeadlineShopTypes } from "@/utils/ShopUtils";

const PAGE_SIZE_KEY = "oms-page-size-global";
const LIST_OPTIONS_SORT_KEY_PREFIX = "oms-list-options-sort-";
const DEFAULT_PAGE_SIZE = 25;

const getPageSize = async (shopId: number | undefined) => {
    const pageSizeStr = localStorage.getItem(PAGE_SIZE_KEY);

    let currentPageSize = pageSizeStr ? parseInt(pageSizeStr, 10) : DEFAULT_PAGE_SIZE;

    if (currentPageSize > 500 && shopId) {
        const shop = globalStore.shopMapping.get(shopId);
        const ownerShop = globalStore.shopMapping.get(shop.ownerShopId);

        const wantToLoadShopId = ownerShop?.id || shop.id;

        const info = await globalStore.getShopAppVersion(wantToLoadShopId);

        if (info.appVersion !== AppVersion.PREMIUM) {
            currentPageSize = 500;
        }
    }

    return currentPageSize;
};

const get = async (shopId: number | undefined, tab: OrderTab) => {
    const pageSize = await getPageSize(shopId);

    return { pageSize };
};

const setPageSize = (shopId: number, pageSize: number) => {
    localStorage.setItem(PAGE_SIZE_KEY, pageSize.toString());
};

/**
 *
 *
 * 1、手工店铺
 *  不缓存，默认选择“导入顺序”排序
 *
 * 3、平台店铺订单
 * 状态tab按company级别进行缓存，每个tab都有一个自己的缓存，如果没有缓存值的情况下按以下面规则选择默认排序方式：
 * “挂起” “未付款”、“已取消” 退款中和预发货 ​​已发货、已完成状态tab选“按下单时间降序”
 * 待发货默认选择“按付款时间升序”
 * 非上述状态tab默认选择“按付款时间降序”
 *
 */
const getOrderSort = async (shopId: number, tab: OrderTab): Promise<OrderSort> => {
    const shop = globalStore.shopMapping.get(shopId);
    const shopType = shop?.type;

    // 手工店铺不缓存 默认选择“导入顺序”排序
    if (shopType === ShopType.MANUAL || shopType === ShopType.INDEPENDENT_MANUAL) {
        return OrderSort.IMPORT;
    }

    const key = `${LIST_OPTIONS_SORT_KEY_PREFIX}_COMPANY_${shop?.companyId || "ALL_SHOP"}_${tab}`;

    const cacheOrderSort = localStorage.getItem(key);

    if (cacheOrderSort === OrderSort.LAST_SHIPPING_TIME_ASC && !ShippingDeadlineShopTypes.includes(shopType)) {
        /** 走默认 */
    } else if (cacheOrderSort === OrderSort.PRODUCT_SKU_COLOR_SIZE && shopId) {
        const shopAdvancedSetting = await globalStore.shopAdvancedSettingStore.ensureAdvancedSetting(shopId);

        if (shopAdvancedSetting?.enabledOrderItemColorSizeSorting) {
            return OrderSort.PRODUCT_SKU_COLOR_SIZE;
        }

        /** 如果店铺没有开启自动识别商品属性颜色尺码，则使用默认排序 */
    } else if (cacheOrderSort) {
        /** 导入顺序降序已经废弃了，临时兼容 */
        if (cacheOrderSort === "IMPORT_DESC") {
            setOrderSort(shopId, tab, OrderSort.IMPORT);

            return OrderSort.IMPORT;
        }

        /** 2025/03/28前异常订单是没有排序方式的，排序的方式被赋值成默认的PAY_TIME_DESC */
        /** 2025/03/28上线后，异常订单只有两种排序方式，所以这里需要修正一下缓存值 */
        if (
            tab === OrderTab.ABNORMAL &&
            ![OrderSort.ABNORMAL_RECORD_TIME_ASC, , OrderSort.ABNORMAL_RECORD_TIME_DESC].includes(
                cacheOrderSort as OrderSort,
            )
        ) {
            return OrderSort.ABNORMAL_RECORD_TIME_DESC;
        }

        return cacheOrderSort as OrderSort;
    }

    if (tab === OrderTab.ABNORMAL) {
        return OrderSort.ABNORMAL_RECORD_TIME_DESC;
    } else if (
        [
            OrderTab.SHIPPED,
            OrderTab.SUCCESS,
            OrderTab.SHIPPED_REFUNDING,
            OrderTab.PENDING_SHIPMENT_REFUNDING,
            OrderTab.PRE_SHIP,
            OrderTab.PENDING_PAYMENT,
            OrderTab.CANCELED,
            OrderTab.ON_HOLD,
        ].includes(tab)
    ) {
        return OrderSort.ORDER_TIME_DESC;
    } else if ([OrderTab.PENDING_SHIPMENT].includes(tab)) {
        return OrderSort.PAY_TIME_ASC;
    } else {
        return OrderSort.PAY_TIME_DESC;
    }
};

const setOrderSort = (shopId: number, tab: OrderTab, orderSort: OrderSort) => {
    const shop = globalStore.shopMapping.get(shopId);
    const shopType = shop?.type;

    const key = `${LIST_OPTIONS_SORT_KEY_PREFIX}_COMPANY_${shop?.companyId || "ALL_SHOP"}_${tab}`;

    // 手工店铺不缓存 优先使用当前选中的值
    if (shopType === ShopType.MANUAL) {
        return;
    }

    localStorage.setItem(key, orderSort);

    console.log(`save order list options sort: ${orderSort}, shop: ${shopId}, tab: ${tab}`);
};

export { get, getPageSize, setPageSize, getOrderSort, setOrderSort };
